import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  ArrowRight,
  Zap,
  FileText,
  Code,
  Clock,
  GitBranch,
  Package,
} from 'lucide-react'

export const Route = createFileRoute('/_site/blog/typst-for-pdf-generation')({
  component: TypstPage,
  head: () => ({
    meta: [
      {
        title: 'Why We Use Typst for PDF Generation — Uptrack',
      },
      {
        name: 'description',
        content:
          'Typst makes production-quality PDFs in milliseconds, with no browser. Why we replaced wkhtmltopdf and Puppeteer with Typst for invoices.',
      },
      {
        property: 'og:title',
        content: 'Why We Use Typst for PDF Generation — Uptrack',
      },
      {
        property: 'og:description',
        content:
          'Typst generates production-quality PDFs in milliseconds without a browser. We replaced Puppeteer with it for invoice generation and never looked back.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/blog-typst-for-pdf-generation.png',
      },
      { property: 'og:type', content: 'article' },
      {
        property: 'og:url',
        content: 'https://uptrack.app/blog/typst-for-pdf-generation',
      },
      { property: 'article:published_time', content: '2026-04-18' },
    ],
    links: [
      {
        rel: 'canonical',
        href: 'https://uptrack.app/blog/typst-for-pdf-generation',
      },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'Why We Use Typst for PDF Generation — Uptrack',
          description:
            'Typst generates production-quality PDFs in milliseconds without a browser. We replaced Puppeteer with it for invoice generation and never looked back.',
          datePublished: '2026-04-18',
          dateModified: '2026-05-22',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: {
            '@type': 'Organization',
            name: 'Uptrack',
            url: 'https://uptrack.app',
          },
          mainEntityOfPage:
            'https://uptrack.app/blog/typst-for-pdf-generation',
        }),
      },
    ],
  }),
})

function TypstPage() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <p className="mb-3 text-sm text-muted-foreground">April 18, 2026</p>
          <h1 className="font-sans mb-6 text-4xl font-semibold">
            Generate PDFs without launching a browser
          </h1>

          {/* Hook */}
          <section className="mb-12">
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              At some point, almost every developer who needs to generate a PDF
              reaches for Puppeteer or wkhtmltopdf. The reasoning makes sense:
              you already know HTML and CSS, so why learn something new? Just
              render the page and screenshot it to PDF.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Then reality sets in. Puppeteer boots a full Chromium instance —
              200–400ms just to initialize, before you've rendered a single
              character. wkhtmltopdf uses a frozen 2013 WebKit and breaks on
              modern CSS. Both are black boxes that produce subtly wrong
              output depending on font rendering, page size, and the phase of
              the moon.
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              For our invoice app 9invoice,
              we needed to generate hundreds of invoices on demand with
              pixel-perfect output. We tried the browser approach. Then we
              found Typst, and we've never looked back.
            </p>
          </section>

          {/* What is Typst */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              What is Typst?
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Typst is a modern typesetting system — a LaTeX replacement built
              from scratch in Rust. You write documents in a scripting language
              that compiles directly to PDF. No browser. No WebKit. No XML. Just
              a fast, programmable engine that takes a template and data and
              outputs a perfectly formatted document.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Think of it as LaTeX with a sane syntax and compile times measured
              in milliseconds rather than minutes. Or think of it as "Markdown
              that can do everything Word can do, and exports to PDF natively."
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              Typst ships as a single binary. No runtime dependencies. No
              TeX distribution to install. No headless Chrome to keep alive.
              You call it, it outputs a PDF, it exits.
            </p>
          </section>

          {/* The problem with traditional methods */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              What's wrong with the traditional approaches
            </h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <Clock className="mt-1 h-5 w-5 shrink-0 text-destructive" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Puppeteer / headless Chrome: 400ms to do nothing
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Chromium takes 200–400ms just to start up. Then you
                    navigate to a URL or load HTML, wait for fonts and images,
                    call <code className="text-sm bg-muted px-1 py-0.5 rounded">page.pdf()</code>,
                    and wait again. For a single invoice on a beefy server,
                    that's easily 700ms–1s. Under load with multiple concurrent
                    requests, you're either queuing them (adding latency) or
                    launching multiple Chromium instances (adding memory and CPU).
                    A 2-core server struggles to render more than a few
                    concurrent PDFs without falling over.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Clock className="mt-1 h-5 w-5 shrink-0 text-destructive" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    wkhtmltopdf: frozen in 2013
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    wkhtmltopdf uses a patched QtWebKit that was abandoned in
                    2015. It doesn't support flexbox properly, doesn't support
                    CSS Grid at all, and has no active maintenance. Your
                    carefully designed invoice template looks correct in every
                    browser and wrong in wkhtmltopdf. You spend hours adding
                    wkhtmltopdf-specific CSS hacks that break the browser
                    preview. You ship a product that works, technically, but
                    every update to your design requires re-testing against a
                    decade-old rendering engine.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Clock className="mt-1 h-5 w-5 shrink-0 text-destructive" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    LaTeX: powerful but punishing
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    LaTeX produces beautiful documents. It's also a 40-year-old
                    macro language with backslash syntax, cryptic error messages,
                    and compile times that feel like the 1980s. A full TeX
                    distribution is gigabytes of software. Generating a simple
                    invoice means learning arcane packages, fighting with
                    whitespace, and debugging errors like{' '}
                    <code className="text-sm bg-muted px-1 py-0.5 rounded">{'! Extra }, or forgotten \\endgroup'}</code>.
                    LaTeX is great for academic papers. It's overkill and
                    undersupported for programmatic document generation.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Why Typst wins */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Why Typst wins for programmatic PDF generation
            </h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <Zap className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Millisecond compile times
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Typst compiles a typical invoice template to PDF in 5–30ms.
                    Not per page. Total. That's 30–100x faster than the Puppeteer
                    approach. On a single server core, you can generate thousands
                    of PDFs per minute. Concurrency stops being a problem because
                    each generation is so fast that requests queue for microseconds,
                    not seconds.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Code className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    A scripting language built for documents
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Typst has real programming constructs — variables, loops,
                    functions, conditionals — built into the template language.
                    You can loop over line items, conditionally show tax sections,
                    format currencies, and compute totals, all inside the template.
                    No need to pre-process data in your backend and inject
                    pre-formatted strings. The template is self-contained logic,
                    not a dumb string interpolation target.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <FileText className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Consistent, pixel-perfect output
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Typst doesn't use a browser engine. It has its own layout
                    engine written in Rust with deterministic behavior. The PDF
                    it outputs on your MacBook is identical to the one it outputs
                    on a Linux server or a Windows CI runner. No font rendering
                    differences. No mysterious extra blank pages. No margin
                    quirks depending on the screen DPI. What you design is
                    exactly what ships.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <GitBranch className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Templates are plain text — version control them
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    A Typst template is a <code className="text-sm bg-muted px-1 py-0.5 rounded">.typ</code> file.
                    Plain text. You commit it to Git, review changes in diffs,
                    roll back when something breaks. Compare this to maintaining
                    an invoice template in Word, or in a WYSIWYG editor inside
                    some SaaS tool. With Typst, your template is code. It has
                    the same review and deployment workflow as everything else.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Package className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Single binary, zero runtime dependencies
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Typst ships as a single self-contained binary. You add it to
                    your server and call it as a subprocess. No npm packages to
                    keep updated. No Chromium version drift. No TeX distribution
                    to install. No Python dependency for WeasyPrint. Your Docker
                    image stays small. Your deployment stays predictable.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* How we use it */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              How we use it in 9invoice
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              In 9invoice, every invoice PDF is generated by calling the Typst CLI as a
              subprocess from our backend. The workflow looks like this:
            </p>

            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">1. Template with injected data</h3>
                <p className="text-lg leading-[1.7] text-body">
                  We maintain a <code className="text-sm bg-muted px-1 py-0.5 rounded">invoice.typ</code> template
                  that reads invoice data from a JSON file passed as an input.
                  The template handles line item loops, currency formatting,
                  conditional tax display, and multi-page layout automatically.
                  Changing the invoice design means editing one plain-text file.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">2. Subprocess call from the backend</h3>
                <p className="text-lg leading-[1.7] text-body">
                  When a user requests an invoice PDF, our server writes the
                  invoice data to a temp file and calls{' '}
                  <code className="text-sm bg-muted px-1 py-0.5 rounded">typst compile invoice.typ output.pdf</code>.
                  The process exits in under 30ms. We stream the resulting PDF
                  directly to the response. No S3 upload. No async job queue.
                  The PDF is ready before the user's browser has finished
                  processing the click.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">3. Fonts bundled with the binary</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Typst embeds fonts directly into the PDF so it looks the same
                  everywhere. We embed Inter for UI text and a monospace font
                  for invoice numbers. No relying on system fonts that vary
                  between machines. No worrying about fonts missing in the
                  Docker container. The PDF is a self-contained artifact.
                </p>
              </div>
            </div>
          </section>

          {/* Honest comparison */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              When Typst might not be the right choice
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Typst is not a drop-in replacement for every PDF use case.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              If you need to convert an existing HTML page to PDF — a webpage,
              a marketing email, a web-designed report — Puppeteer is still the
              right tool. Typst has its own layout model; it doesn't understand
              CSS. You can't take an existing HTML template and run it through
              Typst without rewriting it.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              If your team already has LaTeX expertise and is producing academic
              papers, Typst is a worthy upgrade but not urgent. LaTeX's ecosystem
              of packages (especially for math) is broader.
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              But if you're building document generation into a product — invoices,
              receipts, reports, contracts, certificates — and you're currently
              launching a browser to do it, Typst is worth two hours of evaluation.
              The speed difference alone will surprise you.
            </p>
          </section>

          {/* Getting started */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Getting started in 15 minutes
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Install Typst with a single command:
            </p>
            <pre tabIndex={0} className="mb-4 rounded-lg bg-muted p-4 text-sm overflow-x-auto">
              <code>{`# macOS
brew install typst

# Linux / Windows
# Download from https://github.com/typst/typst/releases`}</code>
            </pre>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Write a minimal invoice template:
            </p>
            <pre tabIndex={0} className="mb-4 rounded-lg bg-muted p-4 text-sm overflow-x-auto">
              <code>{`// invoice.typ
#let data = json("data.json")

#set page(paper: "a4", margin: 2cm)
#set text(font: "Inter", size: 11pt)

= Invoice #data.number

*Bill to:* #data.client.name

#table(
  columns: (1fr, auto, auto),
  [Description], [Qty], [Amount],
  ..data.items.map(i => (
    i.description,
    str(i.qty),
    "$" + str(i.amount),
  )).flatten()
)

*Total: $#data.total*`}</code>
            </pre>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Compile it:
            </p>
            <pre tabIndex={0} className="mb-4 rounded-lg bg-muted p-4 text-sm overflow-x-auto">
              <code>typst compile invoice.typ</code>
            </pre>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              That's it. A PDF appears in under 30ms. No browser. No LaTeX. No
              XML. From there, call it as a subprocess from any language, pass
              in real data, and you have production PDF generation.
            </p>
          </section>

          {/* CTA */}
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <Zap className="mx-auto mb-4 h-8 w-8 text-primary" />
            <h2 className="font-sans mb-3 text-xl font-semibold">
              Keep tabs on what matters
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              While Typst keeps your PDFs fast, Uptrack keeps your services up.
              30-second checks from $3/mo.
            </p>
            <Button size="lg" asChild>
              <Link to="/signup">
                See plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </article>
      </>

      </>
  )
}
